import React, { useState, useEffect } from 'react';
import {
  X, Instagram, Facebook, Linkedin, Youtube,
  Settings, Check, Info, Shield
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter
} from "@/ui/dialog";
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import API from '@/api/api01';
import { toast } from 'sonner';

interface ManageIntegrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: any;
  onSuccess: () => void;
}

export default function ManageIntegrationModal({ open, onOpenChange, integration, onSuccess }: ManageIntegrationModalProps) {
  const [loading, setLoading] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    if (integration) {
      setAccountName(integration.account_name || '');
      setAccountId(integration.account_id || '');
      setAccessToken(integration.access_token || '');
    }
  }, [integration]);

  const handleUpdate = async () => {
    if (!accountName) {
      toast.error("Account name is required");
      return;
    }

    try {
      setLoading(true);
      console.log(`[ManageIntegrationModal] Updating ${integration.platform}...`);
      await API.post('/superadmin/social-integrations', {
        platform: integration.platform,
        account_name: accountName,
        account_id: accountId,
        access_token: accessToken,
        status: 'Connected'
      });
      console.log(`[ManageIntegrationModal] ${integration.platform} updated successfully.`);
      toast.success("Settings updated successfully");
      
      // Close modal FIRST to prevent any race conditions with parent re-render
      onOpenChange(false);
      
      // Then trigger success to refresh data after a small delay to allow modal cleanup
      setTimeout(() => {
        console.log("[ManageIntegrationModal] Triggering onSuccess callback...");
        onSuccess();
      }, 300);
    } catch (err) {
      console.error(`[ManageIntegrationModal] Update failed:`, err);
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  if (!integration) return null;

  const PlatformIcon = () => {
    switch (integration.platform.toLowerCase()) {
      case 'instagram': return <Instagram className="text-[#E1306C]" />;
      case 'facebook': return <Facebook className="text-[#1877F2]" />;
      case 'linkedin': return <Linkedin className="text-[#0A66C2]" />;
      case 'youtube': return <Youtube className="text-[#FF0000]" />;
      default: return <Shield className="text-purple-600" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] w-[95vw] rounded-[2rem] border-none shadow-2xl p-0 overflow-hidden bg-white dark:bg-slate-950">
        <div className="bg-slate-900 p-8 text-white relative overflow-hidden shrink-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <div className="absolute top-[-50%] left-[-20%] w-[140%] h-[140%] bg-[radial-gradient(circle,rgba(124,58,237,0.4)_0%,transparent_70%)]" />
          </div>

          <DialogHeader className="relative z-10">
            <div className="flex items-center gap-4 mb-2">
              <div className="h-12 w-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                <PlatformIcon />
              </div>
              <div>
                <DialogTitle className="text-2xl font-black">{integration.platform} Settings</DialogTitle>
                <DialogDescription className="text-white/50 text-xs font-medium">Manage your {integration.platform} connection parameters.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="absolute top-6 right-6 text-white/30 hover:text-white cursor-pointer z-20" onClick={() => onOpenChange(false)}>
            <X size={18} />
          </div>
        </div>

        <div className="p-8 space-y-6 bg-slate-50/50 dark:bg-slate-900/10">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Account Display Name</Label>
              <Input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="e.g. Main Instagram"
                className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Business Account ID</Label>
              <Input
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="Optional"
                className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Access Token / API Key</Label>
              <Input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="••••••••••••••••"
                className="h-12 rounded-xl border-none bg-white dark:bg-slate-900 shadow-sm focus:ring-2 focus:ring-purple-500 font-semibold"
              />
            </div>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-800/50 flex gap-4 items-center">
            <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white shrink-0">
              <Check size={16} />
            </div>
            <p className="text-[11px] text-emerald-800 dark:text-emerald-300 font-bold">
              Connection is currently active and syncing leads every 15 minutes.
            </p>
          </div>
        </div>

        <DialogFooter className="p-8 pt-0 flex flex-row items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-900/10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl h-12 px-6 font-bold text-slate-400 hover:text-slate-600">
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={loading}
            className="rounded-xl h-12 px-8 bg-purple-600 hover:bg-purple-700 font-black shadow-lg shadow-purple-200"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
